import React from 'react';
import { motion } from 'framer-motion';
import { FaRobot, FaComments, FaChartLine, FaClock, FaFileInvoiceDollar, FaLightbulb, FaCogs } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const AIConsulting = ({ market }) => {
  const b = getBrand(market);
  const ctaLink     = market === 'lu' ? '/services/assessment' : '/contact';

  const useCases = [
    {
      icon: <FaComments size={32} color={b.accent} />,
      title: 'Customer Service Automation',
      description: 'AI chatbots to handle common inquiries 24/7, freeing your team for complex issues.',
      examples: ['Email auto-responses', 'Website chat support', 'FAQ automation']
    },
    {
      icon: <FaFileInvoiceDollar size={32} color={b.accent} />,
      title: 'Document Processing',
      description: 'Extract and process information from invoices, contracts, and forms automatically.',
      examples: ['Invoice data extraction', 'Contract analysis', 'Form processing']
    },
    {
      icon: <FaChartLine size={32} color={b.accent} />,
      title: 'Business Intelligence',
      description: 'Turn your data into actionable insights with AI-powered analytics and reporting.',
      examples: ['Sales forecasting', 'Customer behaviour analysis', 'Performance dashboards']
    },
    {
      icon: <FaClock size={32} color={b.accent} />,
      title: 'Process Automation',
      description: 'Automate repetitive tasks like data entry, scheduling, and follow-ups.',
      examples: ['Email sorting', 'Appointment scheduling', 'Data synchronisation']
    }
  ];

  // 6 benefits → 3-column grid
  const benefits = [
    'Reduce manual work by up to 70%',
    'Improve response times and customer satisfaction',
    'Lower operational costs significantly',
    'Scale your business without adding headcount',
    'Make data-driven decisions faster',
    'Stay competitive in an AI-driven market'
  ];

  // 4 approach steps → 2×2 grid
  const approach = [
    { step: '01', title: 'Discovery',        description: 'We identify time-consuming tasks and pain points in your business.' },
    { step: '02', title: 'Strategy',          description: 'Design AI solutions tailored to your needs and budget.' },
    { step: '03', title: 'Implementation',    description: 'Deploy and integrate AI tools with your existing systems.' },
    { step: '04', title: 'Training & Support', description: 'Train your team and provide ongoing optimisation.' }
  ];

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title={market === 'lu' ? 'AI Consulting for Luxembourg SMBs' : 'AI Consulting & Automation'}
        subtitle="Practical AI solutions that save time, reduce costs, and help your business compete with larger companies."
        ctaText="Explore AI for Your Business"
        ctaLink={ctaLink}
        icon={<FaRobot color="#1A237E" />}
      />

      {/* ── USE CASES — 4 items → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Use Cases</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              How AI can help your business
            </h2>
          </div>

          <div className="grid-4">
            {useCases.map((uc, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }}
                viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 28px' }}
              >
                <div style={{ marginBottom: '16px' }}>{uc.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>
                  {uc.title}
                </h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.75, color: b.textMuted, marginBottom: '14px' }}>
                  {uc.description}
                </p>
                <p style={{ fontSize: b.fz.xs, color: b.isMWO ? 'rgba(232,228,220,0.62)' : '#94a3b8', fontStyle: 'italic' }}>
                  {uc.examples.join(' · ')}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS — 6 items → 3-col grid ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Why AI</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Why businesses choose AI
            </h2>
          </div>

          <div className="grid-6">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }}
                viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}
              >
                <FaLightbulb size={18} color="#F59E0B" className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, fontWeight: 500 }}>{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPROACH — 4 steps → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>How We Work</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Our approach
            </h2>
          </div>

          <div className="grid-4">
            {approach.map((phase, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.35, delay: i * 0.07 }}
                viewport={{ once: true }}
                style={{
                  background: b.bgCard, borderRadius: b.cardRadius,
                  boxShadow: b.cardShadow, padding: '32px 24px', textAlign: 'center'
                }}
              >
                <div className="process-step-num"
                  style={{ background: b.stepBg, border: b.stepBorder, color: b.stepColor }}>
                  {phase.step}
                </div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>
                  {phase.title}
                </h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted }}>{phase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── EXAMPLE (lu only) ── */}
      {market === 'lu' && (
        <section style={{ background: b.bg, padding: b.secPad }}>
          <div style={{ maxWidth: '860px', margin: '0 auto' }}>
            <div style={{ textAlign: b.isMWO ? 'left' : 'center', marginBottom: '24px' }}>
              <FaCogs size={40} color={b.accent} />
            </div>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, marginBottom: '16px', letterSpacing: '-0.02em', textAlign: b.isMWO ? 'left' : 'center' }}>
              Example: AI for a consulting firm
            </h2>
            <p style={{ fontSize: b.fz.base, lineHeight: 1.8, color: b.textMuted, marginBottom: '20px' }}>
              A 5-person consulting firm was spending 10 hours per week manually processing client emails and scheduling meetings. We implemented an AI assistant that:
            </p>
            <ul style={{ listStyle: 'none', paddingLeft: 0, display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {[
                'Categorises and prioritises incoming emails',
                'Suggests responses based on previous communication',
                'Automatically schedules meetings based on availability',
                'Sends follow-up reminders'
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', fontSize: b.fz.base, color: b.text }}>
                  <span style={{ color: b.accent, fontWeight: 700, flexShrink: 0 }}>→</span>
                  {item}
                </li>
              ))}
            </ul>
            <p style={{ fontSize: b.fz.base, lineHeight: 1.8, color: b.isMWO ? b.accent : '#0097A7', fontWeight: 600 }}>
              Result: 8 hours saved per week, faster client response times, and happier team members.
            </p>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Ready to automate your business?"
        body="Let's discuss how AI can work for your specific needs and deliver measurable ROI."
        ctaText="Schedule a Consultation"
        ctaLink={ctaLink}
      />
    </div>
  );
};

export default AIConsulting;
